import { useEffect, useState } from 'react';

import BpkCard from '@skyscanner/backpack-web/bpk-component-card';
import BpkPanel from '@skyscanner/backpack-web/bpk-component-panel';
import { cssModules } from '@skyscanner/backpack-web/bpk-react-utils';

import AtypicalDealsContent from './AtypicalDealsContent';
import PriceBannerExpandIcon from './PriceBannerExpandIcon';

import type { MiniEventTracker } from '@shared/types/frontend/mini-event-tracker';
import type { I18nService } from '@shared/types/i18n';

import STYLES from './AtypicalDeals.module.scss';

import {
  useMinPrice,
  usePriceContext,
} from '@hotels-hotel-detail/data-access/hooks/useHotelOffersContext';
import { TRANSLATE_MAPPING } from '@hotels-hotel-detail/utils/atypical-deals';
import {
  useHotelPriceBand,
  usePriceLevelInsight,
} from '@shared/data-access-frontend/hooks/useAtypicalDealsContext';
import IntersectionObserverWrapper from '@shared/ui/common/IntersectionObserverWrapper';
import AtypicalDealsBottomSheet from '@shared/ui/components/AtypicalDealsBottomSheet';
import AtypicalDealsTitle from '@shared/ui/components/AtypicalDealsTitle';
import { withI18n } from '@shared/ui/contexts/i18n';
import { withMiniEventTracker } from '@shared/ui/contexts/mini-events';
import { buildAdditionalInfoPriceRangesInfo } from '@shared/utils-frontend/mini-events/atypical-deals';
import {
  ATYPICAL_DEALS_TYPE,
  ACTION_TYPE,
  CLICK_AREA,
} from '@shared/utils-frontend/mini-events/constants';
import { buildAdditionalInfoViewButtonClicked } from '@shared/utils-frontend/mini-events/hotels-action';

type Props = {
  i18n: I18nService;
  hotelName?: string;
  miniEventTracker: MiniEventTracker;
  isMobile?: boolean;
};
const cls = cssModules(STYLES);

const AtypicalDeals = ({
  hotelName,
  i18n,
  isMobile,
  miniEventTracker,
}: Props) => {
  const priceBand = useHotelPriceBand();
  const minPrice = useMinPrice();
  const priceContext = usePriceContext();
  const priceLevelInsight = usePriceLevelInsight();
  const {
    culture: { currency },
    translate,
  } = i18n;

  const {
    correlationId,
    dayViewCorrelationId,
    hotelId,
    stay,
    taxAndFeeInclusionPolicy,
  } = priceContext;

  const type = priceLevelInsight?.priceLevel;

  const shouldShowAtypicalDeals = !!(
    priceBand &&
    hotelName &&
    minPrice &&
    type
  );

  const [isOpen, setIsOpen] = useState(false);

  const onOpen = () => {
    miniEventTracker.trackHotelsAction(
      ACTION_TYPE.VIEW_BUTTON_CLICKED,
      buildAdditionalInfoViewButtonClicked(
        CLICK_AREA.PRICE_RANGES_DETAILS_EXPAND_BUTTON,
        correlationId,
      ),
    );
    setIsOpen(true);
  };

  const onClose = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    if (priceBand) {
      miniEventTracker.trackExperimentChokepoint(
        'BD_hotel_level_atypical_deals',
      );
    }
  }, [priceBand, miniEventTracker]);

  if (!shouldShowAtypicalDeals) {
    return null;
  }

  // When on desktop, only show the component when type is 'PRICE_LEVEL_LOW'
  if (type !== 'PRICE_LEVEL_LOW' && !isMobile) {
    return null;
  }

  const trackViewedEvent = (isWidgetSeen = false) => {
    if (shouldShowAtypicalDeals) {
      miniEventTracker.trackHotelsAction(
        ACTION_TYPE.PRICE_RANGES_VIEW,
        buildAdditionalInfoPriceRangesInfo({
          indicativePrice: minPrice,
          type,
          currency,
          correlationId,
          dayViewCorrelationId,
          stay,
          entityId: hotelId,
          isWidgetSeen,
          atypicalDealsType: ATYPICAL_DEALS_TYPE.HOTEL_LEVEL,
          taxAndFeeInclusionPolicy,
          priceBand,
        }),
      );
    }
  };

  return (
    <IntersectionObserverWrapper onElementSeen={() => trackViewedEvent(true)}>
      {isMobile ? (
        <>
          <BpkCard
            className={cls('AtypicalDeals', 'AtypicalDeals__banner')}
            onClick={onOpen}
          >
            <div className={cls('AtypicalDeals__header')}>
              <div className={cls('AtypicalDeals__header--title')}>
                <AtypicalDealsTitle
                  type={type}
                  labelKey={TRANSLATE_MAPPING[type]}
                  small
                />
              </div>
              <div data-testid="expand-icon">
                <PriceBannerExpandIcon isOpen={isOpen} />
              </div>
            </div>
          </BpkCard>
          <AtypicalDealsBottomSheet
            isOpen={isOpen}
            onClose={onClose}
            mainContent={
              <AtypicalDealsContent
                hotelName={hotelName}
                lowestPrice={minPrice}
                type={type}
                isMobile={isMobile}
              />
            }
            explanation={translate('hd_price_range_description_explanation')}
          />
        </>
      ) : (
        <BpkPanel className={cls('AtypicalDeals')}>
          <AtypicalDealsContent
            hotelName={hotelName}
            lowestPrice={minPrice}
            type={type}
            isMobile={isMobile}
          />
        </BpkPanel>
      )}
    </IntersectionObserverWrapper>
  );
};

export default withMiniEventTracker(withI18n(AtypicalDeals));
